import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORES = [
    { key: "leroy_merlin", label: "Leroy Merlin" },
    { key: "castorama", label: "Castorama" },
    { key: "brico_depot", label: "Brico Dépôt" },
];

const EMPTY = { name: "", category: "", unit: "pièce", leroy_merlin: "", castorama: "", brico_depot: "" };

export const AdminMaterials = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [edits, setEdits] = useState({});
    const [search, setSearch] = useState("");

    const load = () =>
        axios.get(`${API}/materials`).then((r) => setItems(r.data.materials || [])).catch(() => {});

    useEffect(() => { load(); }, []);

    const categories = useMemo(() => [...new Set(items.map((m) => m.category))], [items]);
    const filtered = items.filter((m) => m.name.toLowerCase().includes(search.toLowerCase()));

    const toPayload = (v) => ({
        name: v.name, category: v.category, unit: v.unit,
        prices: { leroy_merlin: Number(v.leroy_merlin), castorama: Number(v.castorama), brico_depot: Number(v.brico_depot) },
    });

    const add = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/admin/materials`, toPayload(form));
            setForm(EMPTY);
            toast.success("Matériau ajouté");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur d'ajout");
        }
    };

    const startEdit = (m) => setEdits({
        ...edits,
        [m.id]: { name: m.name, category: m.category, unit: m.unit, leroy_merlin: m.prices.leroy_merlin, castorama: m.prices.castorama, brico_depot: m.prices.brico_depot },
    });

    const save = async (id) => {
        try {
            await axios.put(`${API}/admin/materials/${id}`, toPayload(edits[id]));
            const { [id]: _, ...rest } = edits;
            setEdits(rest);
            toast.success("Matériau mis à jour");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de mise à jour");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ce matériau ?")) return;
        await axios.delete(`${API}/admin/materials/${id}`).catch(() => {});
        toast.success("Matériau supprimé");
        load();
    };

    const setEdit = (id, k, v) => setEdits({ ...edits, [id]: { ...edits[id], [k]: v } });

    return (
        <div className="space-y-6">
            <form onSubmit={add} className="lux-card p-6">
                <div className="tracking-luxe text-[#B89C6A] mb-4">Ajouter un matériau</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end">
                    <input data-testid="material-name" className="lux-input col-span-2" placeholder="Nom *" required
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input data-testid="material-category" className="lux-input" placeholder="Catégorie *" required list="mat-cats"
                        value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                    <datalist id="mat-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                    <input data-testid="material-unit" className="lux-input" placeholder="Unité"
                        value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                    {STORES.map((s) => (
                        <input key={s.key} data-testid={`material-price-${s.key}`} className="lux-input" type="number" min="0.01" step="0.01"
                            placeholder={`${s.label} € *`} required
                            value={form[s.key]} onChange={(e) => setForm({ ...form, [s.key]: e.target.value })} />
                    ))}
                </div>
                <button data-testid="material-add" type="submit" className="btn-champagne mt-4">Ajouter</button>
            </form>

            <input data-testid="material-search" className="lux-input w-full" placeholder="Rechercher un matériau…"
                value={search} onChange={(e) => setSearch(e.target.value)} />

            <div className="space-y-2">
                {filtered.map((m) => {
                    const ed = edits[m.id];
                    return (
                        <div key={m.id} data-testid={`material-row-${m.id}`} className="lux-card p-4">
                            {ed ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-2 items-center">
                                    <input className="lux-input col-span-2 text-[12px]" value={ed.name} onChange={(e) => setEdit(m.id, "name", e.target.value)} />
                                    <input className="lux-input text-[12px]" value={ed.category} onChange={(e) => setEdit(m.id, "category", e.target.value)} />
                                    <input className="lux-input text-[12px]" value={ed.unit} onChange={(e) => setEdit(m.id, "unit", e.target.value)} />
                                    {STORES.map((s) => (
                                        <input key={s.key} className="lux-input text-[12px]" type="number" min="0.01" step="0.01"
                                            value={ed[s.key]} onChange={(e) => setEdit(m.id, s.key, e.target.value)} />
                                    ))}
                                    <button data-testid={`material-save-${m.id}`} onClick={() => save(m.id)} className="btn-champagne text-[10px] py-2">Enregistrer</button>
                                </div>
                            ) : (
                                <div className="flex justify-between items-center gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-[13px]">{m.name}</div>
                                        <div className="text-[10px] text-[#1C1E1D]/40">{m.category} · prix / {m.unit}</div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {STORES.map((s) => (
                                            <span key={s.key} className="text-[12px] text-[#1C1E1D]/70 whitespace-nowrap">
                                                <span className="text-[9px] tracking-wider uppercase text-[#B89C6A] mr-1">{s.label.split(" ")[0]}</span>
                                                {m.prices[s.key]} €
                                            </span>
                                        ))}
                                        <button data-testid={`material-edit-${m.id}`} onClick={() => startEdit(m)} className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]">Modifier</button>
                                        <button data-testid={`material-delete-${m.id}`} onClick={() => del(m.id)} className="text-[11px] tracking-wider uppercase text-red-700/70 hover:text-red-700 underline">Suppr.</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
